/**
 * Onboarding Configuration
 *
 * Centralized definition of all onboarding steps for the application.
 * Each step has a unique ID, metadata, and dependency information.
 */

export enum OnboardingStepId {
  // Account Setup (1-9)
  PROFILE_COMPLETE = 1,
  EMAIL_VERIFIED = 2,
  PHONE_VERIFIED = 3,

  // Domain Selection (10-19)
  SELECT_DOMAIN = 10,

  // Event Onboarding (20-39)
  CREATE_EVENT_ORG = 20,
  EVENT_ORG_BASIC_INFO = 21,
  EVENT_ORG_CONTACT = 22,
  CREATE_FIRST_EVENT = 23,
  EVENT_BASIC_INFO = 24,
  EVENT_TICKETS = 25,
  EVENT_PUBLISH = 26,

  // Restaurant Onboarding (40-59)
  CREATE_RESTAURANT_ORG = 40,
  RESTAURANT_BASIC_INFO = 41,
  RESTAURANT_CONTACT = 42,
  CREATE_FIRST_MENU = 43,
  ADD_MENU_ITEMS = 44,
  CONFIGURE_TABLES = 45,

  // Music Studio Onboarding (60-79)
  CREATE_STUDIO_ORG = 60,
  STUDIO_BASIC_INFO = 61,        // Name, description, logo, cover
  STUDIO_CONTACT = 62,            // Email, phone, address
  STUDIO_AVAILABILITY = 63,       // Working hours, booking rules
  STUDIO_ADD_ROOM = 64,           // At least one recording room (optional)
  STUDIO_ADD_ENGINEER = 65,       // Optional: Add sound engineer
  STUDIO_EQUIPMENT = 66,          // Optional: Add equipment
  STUDIO_BRANDING = 67,           // Optional: Customize theme
  STUDIO_PUBLISH = 68,            // Make studio public

  // Payment Setup (80-89)
  STRIPE_CONNECT = 80,
  PAYMENT_METHOD = 81,

  // Team Setup (90-99)
  INVITE_TEAM = 90,
  SETUP_ROLES = 91,
}

export type DomainType = 'event' | 'restaurant' | 'musicStudio';

export interface OnboardingStepDefinition {
  id: OnboardingStepId;
  name: string;
  description: string;
  category: 'account' | 'domain' | 'event' | 'restaurant' | 'studio' | 'payment' | 'team';
  required: boolean;
  estimatedMinutes: number;
  blocks?: OnboardingStepId[];  // Steps that depend on this
  prerequisite?: OnboardingStepId[];  // Steps required before this
  appliesToDomains?: DomainType[];  // If specified, only applies to these domains
}

export const ONBOARDING_STEPS: Record<OnboardingStepId, OnboardingStepDefinition> = {
  // Account Setup
  [OnboardingStepId.PROFILE_COMPLETE]: {
    id: OnboardingStepId.PROFILE_COMPLETE,
    name: 'Complete Profile',
    description: 'Add your name and basic information',
    category: 'account',
    required: true,
    estimatedMinutes: 2,
    blocks: [OnboardingStepId.SELECT_DOMAIN],
  },

  [OnboardingStepId.EMAIL_VERIFIED]: {
    id: OnboardingStepId.EMAIL_VERIFIED,
    name: 'Verify Email',
    description: 'Confirm your email address',
    category: 'account',
    required: false,
    estimatedMinutes: 5,
  },

  [OnboardingStepId.PHONE_VERIFIED]: {
    id: OnboardingStepId.PHONE_VERIFIED,
    name: 'Verify Phone',
    description: 'Confirm your phone number',
    category: 'account',
    required: false,
    estimatedMinutes: 5,
  },

  // Domain Selection
  [OnboardingStepId.SELECT_DOMAIN]: {
    id: OnboardingStepId.SELECT_DOMAIN,
    name: 'Select Domain',
    description: 'Choose your business type (Event, Restaurant, or Music Studio)',
    category: 'domain',
    required: true,
    estimatedMinutes: 1,
    prerequisite: [OnboardingStepId.PROFILE_COMPLETE],
  },

  // Event Onboarding
  [OnboardingStepId.CREATE_EVENT_ORG]: {
    id: OnboardingStepId.CREATE_EVENT_ORG,
    name: 'Create Event Organization',
    description: 'Initialize your event organization',
    category: 'event',
    required: true,
    estimatedMinutes: 1,
    prerequisite: [OnboardingStepId.SELECT_DOMAIN],
    appliesToDomains: ['event'],
  },

  [OnboardingStepId.EVENT_ORG_BASIC_INFO]: {
    id: OnboardingStepId.EVENT_ORG_BASIC_INFO,
    name: 'Organization Basic Info',
    description: 'Name, description, and branding for your organization',
    category: 'event',
    required: true,
    estimatedMinutes: 5,
    prerequisite: [OnboardingStepId.CREATE_EVENT_ORG],
    appliesToDomains: ['event'],
  },

  [OnboardingStepId.EVENT_ORG_CONTACT]: {
    id: OnboardingStepId.EVENT_ORG_CONTACT,
    name: 'Organization Contact',
    description: 'Contact information and address',
    category: 'event',
    required: true,
    estimatedMinutes: 3,
    prerequisite: [OnboardingStepId.EVENT_ORG_BASIC_INFO],
    appliesToDomains: ['event'],
  },

  [OnboardingStepId.CREATE_FIRST_EVENT]: {
    id: OnboardingStepId.CREATE_FIRST_EVENT,
    name: 'Create First Event',
    description: 'Create your first event',
    category: 'event',
    required: true,
    estimatedMinutes: 3,
    prerequisite: [OnboardingStepId.EVENT_ORG_CONTACT],
    appliesToDomains: ['event'],
  },

  [OnboardingStepId.EVENT_BASIC_INFO]: {
    id: OnboardingStepId.EVENT_BASIC_INFO,
    name: 'Event Details',
    description: 'Event name, date, location, and description',
    category: 'event',
    required: true,
    estimatedMinutes: 5,
    prerequisite: [OnboardingStepId.CREATE_FIRST_EVENT],
    appliesToDomains: ['event'],
  },

  [OnboardingStepId.EVENT_TICKETS]: {
    id: OnboardingStepId.EVENT_TICKETS,
    name: 'Event Tickets',
    description: 'Configure ticket types and pricing',
    category: 'event',
    required: false,
    estimatedMinutes: 5,
    prerequisite: [OnboardingStepId.EVENT_BASIC_INFO],
    appliesToDomains: ['event'],
  },

  [OnboardingStepId.EVENT_PUBLISH]: {
    id: OnboardingStepId.EVENT_PUBLISH,
    name: 'Publish Event',
    description: 'Make your event visible to the public',
    category: 'event',
    required: true,
    estimatedMinutes: 1,
    prerequisite: [OnboardingStepId.EVENT_BASIC_INFO],
    appliesToDomains: ['event'],
  },

  // Restaurant Onboarding
  [OnboardingStepId.CREATE_RESTAURANT_ORG]: {
    id: OnboardingStepId.CREATE_RESTAURANT_ORG,
    name: 'Create Restaurant Organization',
    description: 'Initialize your restaurant',
    category: 'restaurant',
    required: true,
    estimatedMinutes: 1,
    prerequisite: [OnboardingStepId.SELECT_DOMAIN],
    appliesToDomains: ['restaurant'],
  },

  [OnboardingStepId.RESTAURANT_BASIC_INFO]: {
    id: OnboardingStepId.RESTAURANT_BASIC_INFO,
    name: 'Restaurant Basic Info',
    description: 'Name, description, cuisine type, and branding',
    category: 'restaurant',
    required: true,
    estimatedMinutes: 5,
    prerequisite: [OnboardingStepId.CREATE_RESTAURANT_ORG],
    appliesToDomains: ['restaurant'],
  },

  [OnboardingStepId.RESTAURANT_CONTACT]: {
    id: OnboardingStepId.RESTAURANT_CONTACT,
    name: 'Restaurant Contact',
    description: 'Contact information and location',
    category: 'restaurant',
    required: true,
    estimatedMinutes: 3,
    prerequisite: [OnboardingStepId.RESTAURANT_BASIC_INFO],
    appliesToDomains: ['restaurant'],
  },

  [OnboardingStepId.CREATE_FIRST_MENU]: {
    id: OnboardingStepId.CREATE_FIRST_MENU,
    name: 'Create Menu',
    description: 'Create your first menu',
    category: 'restaurant',
    required: true,
    estimatedMinutes: 3,
    prerequisite: [OnboardingStepId.RESTAURANT_CONTACT],
    appliesToDomains: ['restaurant'],
  },

  [OnboardingStepId.ADD_MENU_ITEMS]: {
    id: OnboardingStepId.ADD_MENU_ITEMS,
    name: 'Add Menu Items',
    description: 'Add dishes to your menu',
    category: 'restaurant',
    required: false,
    estimatedMinutes: 10,
    prerequisite: [OnboardingStepId.CREATE_FIRST_MENU],
    appliesToDomains: ['restaurant'],
  },

  [OnboardingStepId.CONFIGURE_TABLES]: {
    id: OnboardingStepId.CONFIGURE_TABLES,
    name: 'Configure Tables',
    description: 'Set up table management',
    category: 'restaurant',
    required: false,
    estimatedMinutes: 5,
    prerequisite: [OnboardingStepId.RESTAURANT_CONTACT],
    appliesToDomains: ['restaurant'],
  },

  // Music Studio Onboarding
  [OnboardingStepId.CREATE_STUDIO_ORG]: {
    id: OnboardingStepId.CREATE_STUDIO_ORG,
    name: 'Create Studio Organization',
    description: 'Initialize your music studio',
    category: 'studio',
    required: true,
    estimatedMinutes: 1,
    prerequisite: [OnboardingStepId.SELECT_DOMAIN],
    appliesToDomains: ['musicStudio'],
  },

  [OnboardingStepId.STUDIO_BASIC_INFO]: {
    id: OnboardingStepId.STUDIO_BASIC_INFO,
    name: 'Studio Basic Info',
    description: 'Studio name, description, logo, and cover image',
    category: 'studio',
    required: true,
    estimatedMinutes: 5,
    prerequisite: [OnboardingStepId.CREATE_STUDIO_ORG],
    appliesToDomains: ['musicStudio'],
  },

  [OnboardingStepId.STUDIO_CONTACT]: {
    id: OnboardingStepId.STUDIO_CONTACT,
    name: 'Studio Contact Information',
    description: 'Email, phone, website, and physical address',
    category: 'studio',
    required: true,
    estimatedMinutes: 3,
    prerequisite: [OnboardingStepId.STUDIO_BASIC_INFO],
    appliesToDomains: ['musicStudio'],
  },

  [OnboardingStepId.STUDIO_AVAILABILITY]: {
    id: OnboardingStepId.STUDIO_AVAILABILITY,
    name: 'Availability Settings',
    description: 'Configure working hours and booking rules',
    category: 'studio',
    required: true,
    estimatedMinutes: 5,
    prerequisite: [OnboardingStepId.STUDIO_CONTACT],
    appliesToDomains: ['musicStudio'],
  },

  [OnboardingStepId.STUDIO_ADD_ROOM]: {
    id: OnboardingStepId.STUDIO_ADD_ROOM,
    name: 'Add Recording Room',
    description: 'Create at least one studio room (optional)',
    category: 'studio',
    required: false,
    estimatedMinutes: 3,
    prerequisite: [OnboardingStepId.STUDIO_AVAILABILITY],
    appliesToDomains: ['musicStudio'],
  },

  [OnboardingStepId.STUDIO_ADD_ENGINEER]: {
    id: OnboardingStepId.STUDIO_ADD_ENGINEER,
    name: 'Add Sound Engineer',
    description: 'Add your sound engineering team (optional)',
    category: 'studio',
    required: false,
    estimatedMinutes: 3,
    prerequisite: [OnboardingStepId.STUDIO_AVAILABILITY],
    appliesToDomains: ['musicStudio'],
  },

  [OnboardingStepId.STUDIO_EQUIPMENT]: {
    id: OnboardingStepId.STUDIO_EQUIPMENT,
    name: 'Add Equipment',
    description: 'List your studio equipment (optional)',
    category: 'studio',
    required: false,
    estimatedMinutes: 5,
    prerequisite: [OnboardingStepId.STUDIO_AVAILABILITY],
    appliesToDomains: ['musicStudio'],
  },

  [OnboardingStepId.STUDIO_BRANDING]: {
    id: OnboardingStepId.STUDIO_BRANDING,
    name: 'Customize Branding',
    description: 'Customize your studio page layout and theme (optional)',
    category: 'studio',
    required: false,
    estimatedMinutes: 5,
    prerequisite: [OnboardingStepId.STUDIO_AVAILABILITY],
    appliesToDomains: ['musicStudio'],
  },

  [OnboardingStepId.STUDIO_PUBLISH]: {
    id: OnboardingStepId.STUDIO_PUBLISH,
    name: 'Publish Studio',
    description: 'Make your studio visible to potential clients',
    category: 'studio',
    required: true,
    estimatedMinutes: 1,
    prerequisite: [OnboardingStepId.STUDIO_AVAILABILITY],
    appliesToDomains: ['musicStudio'],
  },

  // Payment Setup
  [OnboardingStepId.STRIPE_CONNECT]: {
    id: OnboardingStepId.STRIPE_CONNECT,
    name: 'Connect Stripe',
    description: 'Set up payment processing with Stripe',
    category: 'payment',
    required: false,
    estimatedMinutes: 10,
  },

  [OnboardingStepId.PAYMENT_METHOD]: {
    id: OnboardingStepId.PAYMENT_METHOD,
    name: 'Add Payment Method',
    description: 'Add a payment method for subscriptions',
    category: 'payment',
    required: false,
    estimatedMinutes: 5,
  },

  // Team Setup
  [OnboardingStepId.INVITE_TEAM]: {
    id: OnboardingStepId.INVITE_TEAM,
    name: 'Invite Team Members',
    description: 'Invite staff and collaborators',
    category: 'team',
    required: false,
    estimatedMinutes: 5,
  },

  [OnboardingStepId.SETUP_ROLES]: {
    id: OnboardingStepId.SETUP_ROLES,
    name: 'Configure Roles',
    description: 'Review predefined roles and permissions',
    category: 'team',
    required: false,
    estimatedMinutes: 5,
    prerequisite: [OnboardingStepId.INVITE_TEAM],
  },
};

/**
 * Get all steps for a specific domain
 */
export function getStepsForDomain(domain: DomainType): OnboardingStepDefinition[] {
  return Object.values(ONBOARDING_STEPS).filter(
    (step) => !step.appliesToDomains || step.appliesToDomains.includes(domain)
  );
}

/**
 * Get all required steps for a specific domain
 */
export function getRequiredStepsForDomain(domain: DomainType): OnboardingStepDefinition[] {
  return getStepsForDomain(domain).filter((step) => step.required);
}
